/**
 * Responsibility: create, describe and delete sub-collections.
 *
 * The note and the goal are edited here rather than on a card, because they
 * belong to the grouping. Editing them from a card would invite the idea that
 * each card carries its own copy, which is exactly what the data model avoids.
 *
 * Deleting says how many cards it will affect, because the tag comes off all
 * of them and that is not obvious from a list of names. The cards themselves
 * are never deleted, and the wording says so.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import type { CollectionTag } from '../../api/client';
import { useAddTag, useEditTag, useRemoveTag } from '../../api/hooks/useTags';

type Props = {
  open: boolean;
  onClose: () => void;
  tags: CollectionTag[];
};

/** A goal is optional, so an empty box means "no target", not zero. */
function parseGoal(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 1 ? Math.floor(value) : null;
}

export function TagManagerDialog({ open, onClose, tags }: Props) {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [goal, setGoal] = useState('');

  const add = useAddTag();
  const edit = useEditTag();
  const remove = useRemoveTag();

  const busy = add.isPending || edit.isPending || remove.isPending;
  const error = add.error ?? edit.error ?? remove.error;

  const create = () => {
    if (name.trim() === '') return;
    add.mutate(
      { name: name.trim(), note: note.trim(), goal: parseGoal(goal) },
      {
        onSuccess: () => {
          setName('');
          setNote('');
          setGoal('');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Sub-collections</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}

          <Typography variant="body2" color="text.secondary">
            A grouping of cards with its own name and note &mdash; a run you are
            completing, or just a set of cards that go together. A card can be in
            as many as you like.
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 60 } }}
            />
            <TextField
              size="small"
              label="What is it for"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              minRows={2}
            />
            <TextField
              size="small"
              label="Cards to collect"
              helperText="Leave empty if there is no end to it"
              value={goal}
              onChange={(event) => setGoal(event.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            />
            <Button variant="contained" onClick={create} disabled={busy || name.trim() === ''}>
              Create
            </Button>
          </Stack>

          {tags.length > 0 ? <Divider /> : null}

          <Stack spacing={1.5}>
            {tags.map((tag) => (
              <Stack key={tag.id} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    defaultValue={tag.name}
                    // On blur rather than on change: a rename per keystroke
                    // is a write per keystroke, against a quota.
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      if (next !== '' && next !== tag.name) {
                        edit.mutate({ tagId: tag.id, changes: { name: next, clear_goal: false } });
                      }
                    }}
                  />
                  <IconButton
                    aria-label={`Delete ${tag.name}`}
                    disabled={busy}
                    onClick={() => remove.mutate(tag.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  defaultValue={tag.note}
                  placeholder="What is it for"
                  onBlur={(event) => {
                    const next = event.target.value;
                    if (next !== tag.note) {
                      edit.mutate({ tagId: tag.id, changes: { note: next, clear_goal: false } });
                    }
                  }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Goal"
                    defaultValue={tag.goal === null ? '' : String(tag.goal)}
                    sx={{ width: 120 }}
                    onBlur={(event) => {
                      const next = parseGoal(event.target.value);
                      if (next === tag.goal) return;
                      // Clearing needs its own signal: goal null already
                      // means "not mentioned" on the wire.
                      edit.mutate({
                        tagId: tag.id,
                        changes:
                          next === null
                            ? { clear_goal: true }
                            : { goal: next, clear_goal: false },
                      });
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {tag.held} {tag.held === 1 ? 'card' : 'cards'} in this
                    {tag.held > 0 ? ' — deleting keeps the cards, only the grouping goes' : ''}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
